import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HotkeyConfig } from '../../hooks/useHotkeys';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  panelClassName?: string; // classes applied to the inner panel
  overlayClassName?: string; // classes applied to the overlay
  closeOnOverlay?: boolean; // default true
  closeOnEsc?: boolean; // default true
  // Optional enhancements used by admin screens
  title?: string; // for future header render; currently unused
  hotkeys?: HotkeyConfig[]; // register hotkeys while modal is open
  maxWidth?: string; // e.g., '4xl' to override max width
}

declare global {
  interface Window { __modalCount?: number }
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  panelClassName = 'bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto',
  overlayClassName = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4',
  closeOnOverlay = true,
  closeOnEsc = true,
  hotkeys,
  maxWidth,
}) => {
  // Register listeners and manage scroll lock only while open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEsc && event.key === 'Escape') {
        onClose();
        return;
      }

      if (!hotkeys || hotkeys.length === 0) return;

      // ignore when typing in inputs
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        (event.target instanceof HTMLElement && event.target.contentEditable === 'true')
      ) {
        return;
      }

      for (const hk of hotkeys) {
        const keyMatches = event.key.toLowerCase() === hk.key.toLowerCase();
        const ctrlMatches = (hk.ctrlKey ?? false) === event.ctrlKey;
        const metaMatches = (hk.metaKey ?? false) === event.metaKey;
        const altMatches = (hk.altKey ?? false) === event.altKey;
        const shiftMatches = (hk.shiftKey ?? false) === event.shiftKey;
        if (keyMatches && ctrlMatches && metaMatches && altMatches && shiftMatches) {
          if (hk.condition && !hk.condition()) continue;
          event.preventDefault();
          hk.action();
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Body scroll lock with ref count
    window.__modalCount = (window.__modalCount || 0) + 1;
    document.documentElement.classList.add('overflow-hidden');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.__modalCount = Math.max(0, (window.__modalCount || 1) - 1);
      if (!window.__modalCount) {
        document.documentElement.classList.remove('overflow-hidden');
      }
    };
  }, [isOpen, closeOnEsc, onClose, hotkeys]);

  const overlayProps = closeOnOverlay
    ? { onClick: onClose }
    : {};

  const panelProps = {
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  };

  const finalPanelClassName = `${panelClassName}${maxWidth ? ` max-w-${maxWidth}` : ''}`;

  const content = (
    <div className={overlayClassName} {...overlayProps}>
      <div className={finalPanelClassName} {...panelProps}>
        {children}
      </div>
    </div>
  );

  // Render at document.body to escape any stacking context issues
  if (!isOpen) return null;
  return ReactDOM.createPortal(content, document.body);
};

export default Modal;
